import React, { useEffect, useState } from "react";
import "../Css/SuccessPage.css";
import BrandLoader from '../Component/Loading/BrandLoader';

import { Link, useParams } from "react-router-dom";
import {
    FiAlertTriangle,
    FiArrowLeft,
    FiArrowRight,
    FiCreditCard,
    FiPackage,
    FiMapPin,
    FiRefreshCw,
    FiShoppingBag,
} from "react-icons/fi";

import { get } from "../services/ApiRouting";
import moment from "moment";
import { useCurrency } from "../services/locationService";

const FailedPage = () => {
    const { format: formatCurrency } = useCurrency();

    const { order_id } = useParams();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // =========================================================
    // FETCH FAILED ORDER
    // =========================================================

    const fetchOrder = async () => {

        try {

            setLoading(true);
            setError("");

            const response = await get(
                `/payments/orders?order_id=${order_id}`
            );

            console.log("Failed Order Response:", response);

            if (
                response?.success &&
                response?.orders &&
                response.orders.length > 0
            ) {
                setOrder(response.orders[0]);
            } else {
                setError("Order details could not be found.");
            }

        } catch (err) {

            console.error("Failed Order Error:", err);

            setError(
                err?.response?.data?.message ||
                "Unable to load order details."
            );

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        if (order_id) {
            fetchOrder();
        }

    }, [order_id]);


    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {
        return <BrandLoader message="Loading order details..." />;
    }


    // =========================================================
    // ERROR
    // =========================================================

    if (error || !order) {

        return (
            <section className="success-page failed-page">

                <div className="success-container">

                    <div className="failed-error-card">

                        <div className="failed-error-icon">
                            <FiAlertTriangle />
                        </div>

                        <h2>Unable to Load Order</h2>

                        <p>
                            {error || "Something went wrong while loading your order."}
                        </p>

                        <div className="failed-actions">

                            <button
                                className="failed-btn primary"
                                onClick={fetchOrder}
                            >
                                <FiRefreshCw />
                                Try Again
                            </button>

                            <Link
                                to="/"
                                className="failed-btn secondary"
                            >
                                <FiArrowLeft />
                                Back to Home
                            </Link>

                        </div>

                    </div>

                </div>

            </section>
        );
    }


    // =========================================================
    // ORDER DATA
    // =========================================================

    const {
        order_id: apiOrderId,
        order_number,
        subtotal,
        shipping_amount,
        discount_amount,
        total_amount,
        currency,
        payment_method,
        payment_status,
        order_status,

        order_created_at,

        user_name,
        user_email,
        user_phone,

        delivery_phone,
        address_line1,
        city,
        state,
        postal_code,
        country_name,

        items = [],
    } = order;


    // =========================================================
    // TAX
    // =========================================================

    const subtotalNumber = Number(subtotal || 0);

    const basePrice =
        subtotalNumber > 0
            ? subtotalNumber / 1.18
            : 0;

    const tax =
        subtotalNumber - basePrice;


    // =========================================================
    // FORMAT MONEY
    // =========================================================

    const formatPrice = formatCurrency;


    // =========================================================
    // PAYMENT FAILED TEXT
    // =========================================================

    const paymentFailed =
        String(payment_status || "").toLowerCase() !== "paid";


    return (
        <section className="success-page failed-page">

            <div className="success-container">

                {/* =================================================
                    FAILED HERO
                ================================================= */}

                <div className="success-hero failed-hero">

                    <div className="failed-hero-icon">
                        <FiAlertTriangle />
                    </div>

                    <div className="failed-hero-content">

                        <span className="failed-eyebrow">
                            PAYMENT UNSUCCESSFUL
                        </span>

                        <h1>
                            Order Payment Failed
                        </h1>

                        <p>
                            Unfortunately, we couldn't complete the payment
                            for your order. Your order has not been confirmed.
                        </p>

                    </div>

                </div>


                {/* =================================================
                    ORDER HEADER
                ================================================= */}

                <div className="order-meta failed-order-meta">

                    <div className="order-meta-item">

                        <div className="order-meta-icon">
                            <FiPackage />
                        </div>

                        <div>
                            <span>Order Number</span>
                            <strong>
                                {order_number || `#${apiOrderId}`}
                            </strong>
                        </div>

                    </div>


                    <div className="order-meta-item">

                        <div className="order-meta-icon">
                            <FiCreditCard />
                        </div>

                        <div>
                            <span>Payment Status</span>

                            <strong className="failed-status-text">
                                {payment_status || "Failed"}
                            </strong>
                        </div>

                    </div>


                    <div className="order-meta-item">

                        <div className="order-meta-icon">
                            <FiPackage />
                        </div>

                        <div>
                            <span>Order Status</span>

                            <strong>
                                {order_status || "Not Confirmed"}
                            </strong>
                        </div>

                    </div>


                    <div className="order-meta-item">

                        <div className="order-meta-icon">
                            <FiCreditCard />
                        </div>

                        <div>
                            <span>Payment Method</span>

                            <strong>
                                {payment_method || "Online Payment"}
                            </strong>
                        </div>

                    </div>

                </div>


                {/* =================================================
                    DATE
                ================================================= */}

                <div className="failed-date-row">

                    <span>Order attempted on</span>

                    <strong>
                        {order_created_at
                            ? moment(order_created_at).format(
                                "DD MMM YYYY, hh:mm A"
                            )
                            : "N/A"}
                    </strong>

                </div>


                {/* =================================================
                    MAIN GRID
                ================================================= */}

                <div className="success-grid">


                    {/* =================================================
                        LEFT SIDE
                    ================================================= */}

                    <div className="success-left">


                        {/* =============================================
                            PAYMENT ERROR CARD
                        ============================================= */}

                        <div className="failed-message-card">

                            <div className="failed-message-icon">
                                <FiCreditCard />
                            </div>

                            <div>

                                <h3>
                                    We couldn't process your payment
                                </h3>

                                <p>
                                    No successful payment was received for
                                    this order. If money was deducted from
                                    your account, please wait for your bank
                                    or payment provider to process the
                                    reversal.
                                </p>

                            </div>

                        </div>


                        {/* =============================================
                            PRODUCTS
                        ============================================= */}

                        <div className="products-card">

                            <div className="card-heading">

                                <div>
                                    <span className="section-kicker">
                                        ORDER ITEMS
                                    </span>

                                    <h2>
                                        Your Selected Products
                                    </h2>
                                </div>

                                <span className="item-count">
                                    {items.length}{" "}
                                    {items.length === 1
                                        ? "Item"
                                        : "Items"}
                                </span>

                            </div>


                            <div className="product-list">

                                {items.length > 0 ? (

                                    items.map((item) => {

                                        const image =
                                            item.order_item_image ||
                                            item.image ||
                                            "/placeholder-product.png";

                                        return (

                                            <div
                                                className="product-row"
                                                key={item.order_item_id}
                                            >

                                                <div className="product-image">

                                                    <img
                                                        src={image}
                                                        alt={
                                                            item.product_name ||
                                                            "Product"
                                                        }
                                                        onError={(e) => {
                                                            e.currentTarget.src =
                                                                "/placeholder-product.png";
                                                        }}
                                                    />

                                                </div>


                                                <div className="product-info">

                                                    <span className="product-code">
                                                        {item.product_code ||
                                                            `PRODUCT #${item.product_id}`}
                                                    </span>

                                                    <h3>
                                                        {item.product_name}
                                                    </h3>


                                                    <div className="product-options">

                                                        {item.color_name && (
                                                            <span>
                                                                <b>Color</b>
                                                                {item.color_name}
                                                            </span>
                                                        )}

                                                        {item.size_name && (
                                                            <span>
                                                                <b>Size</b>
                                                                {item.size_name}
                                                            </span>
                                                        )}

                                                        <span>
                                                            <b>Qty</b>
                                                            {item.quantity}
                                                        </span>

                                                    </div>

                                                </div>


                                                <div className="product-price">

                                                    <span>
                                                        Price
                                                    </span>

                                                    <strong>
                                                        {formatPrice(
                                                            item.price
                                                        )}
                                                    </strong>

                                                </div>

                                            </div>

                                        );

                                    })

                                ) : (

                                    <div className="empty-products">
                                        No product information available.
                                    </div>

                                )}

                            </div>

                        </div>


                        {/* =============================================
                            CUSTOMER / DELIVERY
                        ============================================= */}

                        <div className="delivery-card">

                            <div className="card-heading">

                                <div>

                                    <span className="section-kicker">
                                        CUSTOMER DETAILS
                                    </span>

                                    <h2>
                                        Delivery Information
                                    </h2>

                                </div>

                                <FiMapPin />

                            </div>


                            <div className="delivery-content">

                                <div className="delivery-details">

                                    <h3>
                                        {user_name || "Customer"}
                                    </h3>

                                    {user_phone && (
                                        <p>
                                            {user_phone}
                                        </p>
                                    )}

                                    {user_email && (
                                        <p>
                                            {user_email}
                                        </p>
                                    )}

                                    <div className="address-box">

                                        <FiMapPin />

                                        <div>

                                            <strong>
                                                Delivery Address
                                            </strong>

                                            <p>
                                                {address_line1 || ""}
                                                {city
                                                    ? `, ${city}`
                                                    : ""}
                                                {state
                                                    ? `, ${state}`
                                                    : ""}
                                                {postal_code
                                                    ? ` - ${postal_code}`
                                                    : ""}
                                                {country_name
                                                    ? `, ${country_name}`
                                                    : ""}
                                            </p>

                                            {delivery_phone && (
                                                <span>
                                                    Contact:{" "}
                                                    {delivery_phone}
                                                </span>
                                            )}

                                        </div>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        RIGHT SIDE
                    ================================================= */}

                    <aside className="success-right">


                        {/* =============================================
                            PAYMENT SUMMARY
                        ============================================= */}

                        <div className="summary-card failed-summary">

                            <div className="summary-heading">

                                <span>
                                    PAYMENT SUMMARY
                                </span>

                                <FiCreditCard />

                            </div>


                            <div className="summary-line">

                                <span>
                                    Subtotal
                                </span>

                                <strong>
                                    {formatPrice(subtotal)}
                                </strong>

                            </div>


                            <div className="summary-line">

                                <span>
                                    Shipping
                                </span>

                                <strong>
                                    {Number(shipping_amount || 0) === 0
                                        ? "FREE"
                                        : formatPrice(shipping_amount)}
                                </strong>

                            </div>


                            <div className="summary-line">

                                <span>
                                    Discount
                                </span>

                                <strong>
                                    - {formatPrice(discount_amount)}
                                </strong>

                            </div>


                            <div className="summary-line">

                                <span>
                                    Base Price
                                </span>

                                <strong>
                                    {formatPrice(basePrice)}
                                </strong>

                            </div>


                            <div className="summary-line">

                                <span>
                                    GST (18%)
                                </span>

                                <strong>
                                    {formatPrice(tax)}
                                </strong>

                            </div>


                            <div className="summary-divider"></div>


                            <div className="summary-total">

                                <span>
                                    Amount Attempted
                                </span>

                                <strong>
                                    {formatPrice(total_amount)}
                                </strong>

                            </div>


                            <div className="failed-payment-label">

                                <FiAlertTriangle />

                                <span>
                                    Payment not completed
                                </span>

                            </div>

                        </div>


                        {/* =============================================
                            WHAT TO DO
                        ============================================= */}

                        <div className="failed-help-card">

                            <span className="section-kicker">
                                WHAT'S NEXT?
                            </span>

                            <h3>
                                Don't worry
                            </h3>

                            <p>
                                You can return to shopping and try placing
                                the order again. If your bank account was
                                charged, the amount should be reversed
                                according to your payment provider's policy.
                            </p>

                            <div className="failed-help-list">

                                <div>
                                    <span>01</span>
                                    <p>
                                        Check your payment details
                                    </p>
                                </div>

                                <div>
                                    <span>02</span>
                                    <p>
                                        Make sure your bank transaction is
                                        successful
                                    </p>
                                </div>

                                <div>
                                    <span>03</span>
                                    <p>
                                        Try placing the order again
                                    </p>
                                </div>

                            </div>

                        </div>

                    </aside>

                </div>


                {/* =================================================
                    ACTIONS
                ================================================= */}

                <div className="failed-bottom-actions">

                    <Link
                        to="/"
                        className="failed-action-btn secondary"
                    >
                        <FiArrowLeft />
                        Back to Home
                    </Link>


                    <Link
                        to="/"
                        className="failed-action-btn primary"
                    >
                        <FiShoppingBag />
                        Continue Shopping
                        <FiArrowRight />
                    </Link>

                </div>


                {/* =================================================
                    FOOTER MESSAGE
                ================================================= */}

                <div className="failed-footer-message">

                    <span>
                        Order #{apiOrderId}
                    </span>

                    <p>
                        Need help with your payment? Please keep your
                        order number handy when contacting support.
                    </p>

                </div>

            </div>

        </section>
    );
};

export default FailedPage;